import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Grid, LinearProgress, Typography } from '@mui/material';
import {
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    CheckCircle as CheckCircleIcon,
    Create as DrawIcon,
    Article as ArticleIcon,
    Groups as GroupsIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import SignaturePad from 'react-signature-canvas';
import moment from 'moment';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import { useProfilContext } from 'src/Contexts/ProfilContexte';
import ButtonField from 'src/Components/Ui/ButtonField';
import FormInput from 'src/Components/Ui/FormInput';
import Settings from 'src/Components/Settings';
import ImageRepository from 'src/Repositories/ImageRepository';
// import TeachrRepository from 'src/Repositories/TeachrRepository';
import { userSetCurrentProcuration } from 'src/Redux/Actions/UserActions';
import { fullNameChild, prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import {
    isBrandActive,
    ENV_VERIFIED_TRAINER,
} from 'src/config/environment.config';
import MentionInput from 'src/Components/Ui/MentionInput';
import { useLocation } from 'react-router-dom';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;
const PDF_PATH_VT = `${process.env.PUBLIC_URL}/VERIFIED-TRAINER-SAP-REGLEMENT-INTERIEUR-v2.pdf`;
const PDF_PATH_SCOOP = `${process.env.PUBLIC_URL}/VERIFIED-TRAINER-SAP-REGLEMENT-INTERIEUR-v2-SCOOP.pdf`;
const MENTION_1_EXPECTED = 'bon pour adhésion';
const MENTION_2_EXPECTED = 'bon pour mandat de facturation';
const MENTION_LU_EXPECTED = 'lu et approuvé';

type SignStep = 'signature' | 'mention1' | 'mention2' | 'done';

const buildAddress = (addr: any): string => {
    const parts = [
        addr.numeroVoie,
        addr.typeVoie,
        addr.libelleVoie,
        addr.complement,
        addr.lieuDit,
        addr.codePostal,
        addr.ville,
    ].filter(Boolean);
    return parts.join(' ');
};
const buildDate = (date: any) => {
    return `${moment(date).format('DD')}    ${moment(date).format('MM')}    ${moment(date).format('YYYY')}`
}

interface BuildPdfOptions {
    signatureDataURL?: string | null;
    withMentions?: boolean;
    faitA?: string;
    pdfPath?: string;
    isVerifiedTrainer?: boolean;
}

const buildFilledPdf = async (formData: any, options: BuildPdfOptions = {}): Promise<Blob> => {
    const {
        signatureDataURL = null,
        withMentions = false,
        faitA,
        pdfPath = PDF_PATH_VT,
        isVerifiedTrainer = true,
    } = options;

    const res = await fetch(pdfPath);
    const arrayBuffer = await res.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true, throwOnInvalidObject: false });
    const form = pdfDoc.getForm();
    const pages = pdfDoc.getPages();

    const fill = (name: string, value: string) => {
        try {
            form.getTextField(name).setText(value);
        } catch { /* field may not exist */ }
    };

    if (isVerifiedTrainer) {
        // VERIFIED-TRAINER-SAP-REGLEMENT-INTERIEUR-v2.pdf fields
        fill('fullname_denomination', formData.denominationSociale ?? '');
        fill('address', buildAddress(formData.address ?? {}));
        fill('rcs', formData.immatriculeRCS ?? '');
        fill('siren', formData.siren ?? '');
        fill('mail', formData.email ?? '');
        fill('phone', formData.telephone ?? '');
        fill('city', faitA ?? formData.faitA ?? '');
        fill('date', moment().format('DD/MM/YYYY'));
        fill('name', formData.nom ?? '');
        fill('prenom', formData.prenom ?? '');
        fill('prenom', formData.prenom ?? '');

        fill('code', formData.code ?? '');
        fill('date_naissance', formData.date_naissance ?? '');
        fill('lieu_naissance', formData.lieu_naissance ?? '');
        fill('mention', withMentions ? 'Bon pour adhésion' : '');
        fill('mention_lu', withMentions ? 'Lu et approuvé' : '');
        fill('mention_mandat', withMentions ? 'Bon pour mandat de facturation' : '');

        if (signatureDataURL) {
            try {
                const allFields = form.getFields();
                const sigField = allFields.find(f => f.getName().toLowerCase().includes('signature'));
                if (sigField) {
                    const imageBytes = await fetch(signatureDataURL).then(r => r.arrayBuffer());
                    const image = await pdfDoc.embedPng(imageBytes);
                    const imageWidth = 100;
                    const imageHeight = 40;
                    pages[8].drawImage(image, {
                        x: 403.5 - imageWidth / 2,
                        y: 561.7 - imageHeight / 2,
                        width: imageWidth,
                        height: imageHeight,
                    });
                    pages[10].drawImage(image, {
                        x: 410.4 - imageWidth / 2,
                        y: 601.8 - imageHeight / 2,
                        width: imageWidth,
                        height: imageHeight,
                    });
                }
            } catch (e) {
                console.error('Could not draw signature on VT PDF:', e);
            }
        }
    } else {
        // VERIFIED-TRAINER-SAP-REGLEMENT-INTERIEUR-v2-SCOOP.pdf fields (same structure as VT)
        fill('fullname_denomination', formData.denominationSociale ?? '');
        fill('address', buildAddress(formData.address ?? {}));
        fill('rcs', formData.immatriculeRCS ?? '');
        fill('siren', formData.siren ?? '');
        fill('mail', formData.email ?? '');
        fill('phone', formData.telephone ?? '');
        fill('city', faitA ?? formData.faitA ?? '');
        fill('date', moment().format('DD/MM/YYYY'));
        fill('full_name', fullNameChild(formData.prenom, formData.nom));
        fill('code', formData.code);
        fill('date_naissance', formData.dateNaissance ? buildDate(formData.dateNaissance) : '');
        fill('lieu_naissance', formData.villeNaissance ?? '');
        fill('mention', withMentions ? 'Bon pour adhésion' : '');
        fill('mention_lu', withMentions ? 'Lu et approuvé' : '');

        fill('name_0TFT', formData.nom);
        fill('prenom_POAI', formData.prenom);
        fill('mention_mandat_5PF2', withMentions ? 'Bon pour mandat de facturation' : '');

        if (signatureDataURL) {
            try {
                const allFields = form.getFields();
                const sigField = allFields.find(f => f.getName().toLowerCase().includes('signature'));
                if (sigField) {
                    const imageBytes = await fetch(signatureDataURL).then(r => r.arrayBuffer());
                    const image = await pdfDoc.embedPng(imageBytes);
                    const imageWidth = 100;
                    const imageHeight = 40;
                    pages[8].drawImage(image, {
                        x: 403.5 - imageWidth / 2,
                        y: 561.7 - imageHeight / 2,
                        width: imageWidth,
                        height: imageHeight,
                    });
                    pages[43].drawImage(image, {
                        x: 410.4 - imageWidth / 2,
                        y: 601.8 - imageHeight / 2,
                        width: imageWidth,
                        height: imageHeight,
                    });
                }
            } catch (e) {
                console.error('Could not draw signature on SCOOP PDF:', e);
            }
        }
    }

    try { form.flatten(); } catch { /* some PDFs resist flattening */ }

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
};

// ---- Sub-components ----

interface DataPreviewProps {
    formData: any;
    primaryHex: string;
    fontFamily?: string;
    isVerifiedTrainer?: boolean;
    faitA?: string;
}

const DataPreview: React.FC<DataPreviewProps> = ({ formData, primaryHex, fontFamily, isVerifiedTrainer = true, faitA }) => {

    if (!formData) return null;
    const address = buildAddress(formData.address ?? {});

    const infoRow = (label: string, value: string) => (
        <Box key={label} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ minWidth: 140, fontFamily }}>
                {label}
            </Typography>
            <Typography variant="caption" color="text.primary" fontWeight="500" sx={{ fontFamily, wordBreak: 'break-word' }}>
                {value || '-'}
            </Typography>
        </Box>
    );

    if (isVerifiedTrainer) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <BusinessIcon sx={{ color: primaryHex, fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight="700" sx={{ color: primaryHex, fontFamily }}>Société</Typography>
                        </Box>
                        {infoRow('Dénomination', formData.denominationSociale)}
                        {infoRow('SIREN', formData.siren)}
                        {infoRow('RCS', formData.immatriculeRCS)}
                        {infoRow('Adresse', address)}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <PersonIcon sx={{ color: '#0284c7', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#0284c7', fontFamily }}>Représentant</Typography>
                        </Box>
                        {infoRow('Nom', formData.nom)}
                        {infoRow('Prénom', formData.prenom)}
                        {infoRow('Email', formData.email)}
                        {infoRow('Téléphone', formData.telephone)}
                        {infoRow('Fait à', formData.faitA)}
                        {infoRow('Date', moment().format('DD/MM/YYYY'))}
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PersonIcon sx={{ color: '#0284c7', fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#0284c7', fontFamily }}>Identité</Typography>
                    </Box>
                    {infoRow('Nom', formData.nom)}
                    {infoRow('Prénom', formData.prenom)}
                    {infoRow('Date de naissance', formData.dateNaissance ? moment(formData.dateNaissance).format('DD/MM/YYYY') : '')}
                    {infoRow('Ville de naissance', formData.villeNaissance)}
                    {infoRow('Email', formData.email)}
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <BusinessIcon sx={{ color: primaryHex, fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: primaryHex, fontFamily }}>Établissement</Typography>
                    </Box>
                    {infoRow('SIREN', formData.siren)}
                    {infoRow('Adresse', address)}
                    {infoRow('Fait à', faitA || '')}
                    {infoRow('Date', moment().format('DD/MM/YYYY'))}
                </Box>
            </Grid>
        </Grid>
    );
};


// ---- Main component ----
const CooperativeDocumentStep: React.FC = () => {
    const { nextStep, teachr, dispatch } = useProfilContext();
    const { brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const fontFamily = brandConfig.fontFamily;

    const isVerifiedTrainer = isBrandActive(ENV_VERIFIED_TRAINER);
    const pdfPath = isVerifiedTrainer ? PDF_PATH_VT : PDF_PATH_SCOOP;
    const formData = useSelector((state: any) => state.user?.cooperativeFormData);

    const [numPages, setNumPages] = useState<number>(0);
    const [pdfWidth, setPdfWidth] = useState(600);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const hasScrolledToEnd = scrollProgress >= 99.5;

    const [signStep, setSignStep] = useState<SignStep>('signature');
    const [faitA, setFaitA] = useState(formData?.faitA || '');
    const [signatureDataURL, setSignatureDataURL] = useState<string | null>(null);
    const [showSignPad, setShowSignPad] = useState(true);
    const padRef = useRef<SignaturePad>(null);

    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);

    const [isGenerating, setIsGenerating] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        const update = () => {
            const container = scrollRef.current;
            if (container) setPdfWidth(Math.min(container.clientWidth - 16, 800));
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => {
            const max = el.scrollHeight - el.clientHeight;
            if (max <= 0) return;
            const progress = Math.min((el.scrollTop / max) * 100, 100);
            setScrollProgress(prev => Math.max(prev, progress));
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    const handleSignatureClear = () => {
        padRef.current?.clear();
        setSignatureDataURL(null);
        setShowSignPad(true);
    };

    const handleSignatureTrim = useCallback(() => {
        const url = padRef.current?.getTrimmedCanvas().toDataURL('image/png');
        if (url) setSignatureDataURL(url);
    }, []);

    const handleSignatureValidate = () => {
        if (!signatureDataURL) return;
        setShowSignPad(false);
        setSignStep('mention1');
    };

    useEffect(() => {
        if (!formData) return;
        let objectUrl: string | null = null;
        setIsLoadingPreview(true);
        buildFilledPdf(formData, { pdfPath, isVerifiedTrainer })
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                setPreviewBlobUrl(objectUrl);
            })
            .catch(() => { /* fall back to raw PDF */ })
            .finally(() => setIsLoadingPreview(false));
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [formData, showSignPad]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async () => {

        setIsGenerating(true);
        try {
            const blob = await buildFilledPdf(formData, {
                signatureDataURL,
                withMentions: true,
                faitA: faitA,
                pdfPath,
                isVerifiedTrainer,
            });
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            const imageResponse = await ImageRepository.createImage(
                new File([blob], 'proof-mandat-scoop', { type: 'application/pdf' })
            );

            const updatedTeachr = { ...teachr } as any;
            updatedTeachr['proofMandat'] = imageResponse['@id'];
            await updateTeachrData(updatedTeachr, dispatch);

            setSignStep('done');
        } catch (e) {
            console.error('Erreur génération PDF coopérative:', e);
        } finally {
            setIsGenerating(false);
        }
    };

    const sectionTitle = (icon: React.ReactNode, title: string, subtitle?: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: `${primaryHex}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {React.cloneElement(icon as React.ReactElement, { sx: { color: primaryHex, fontSize: 20 } })}
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight="700" fontFamily={fontFamily} color="text.primary">{title}</Typography>
                {subtitle && <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>{subtitle}</Typography>}
            </Box>
        </Box>
    );

    // Signing step indicators - both VT and SCOOP have 3 steps
    const stepIndicators = isVerifiedTrainer
        ? [
            { key: 'signature', label: '1. Signature' },
            { key: 'mention1', label: '2. Mention adhésion' },
            { key: 'mention2', label: '3. Mention mandat' },
        ]
        : [
            { key: 'signature', label: '1. Signature' },
            { key: 'mention_lu', label: '2. Lu et approuvé' },
            { key: 'mention_mandat_5PF2', label: '3. Mention mandat' },
        ];
    const stepOrder: SignStep[] = ['signature', 'mention1', 'mention2', 'done'];

    const docTitle = isVerifiedTrainer
        ? 'Règlement intérieur de Verified Trainer SAP'
        : 'Règlement intérieur de la coopérative';

    const docSubtitle = "Lisez attentivement le document puis signez pour finaliser votre adhésion.";

    const docPreviewSubtitle = 'Ces données apparaîtront dans le règlement intérieur';

    return (
        <Settings
            title={docTitle}
            subtitle={docSubtitle}
            icon={<GroupsIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* ── 1. Data summary ── */}
            <Grid item xs={12}>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: `${primaryHex}30`, bgcolor: `${primaryHex}08` }}>
                    {sectionTitle(<ArticleIcon />, 'Informations pré-remplies dans le document', docPreviewSubtitle)}
                    <DataPreview formData={formData} primaryHex={primaryHex} fontFamily={fontFamily} isVerifiedTrainer={true} faitA={faitA} />
                </Box>
            </Grid>

            {/* ── 2. PDF reader ── */}
            <Grid item xs={12}>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    {sectionTitle(<ArticleIcon />, 'Règlement intérieur', "Faites défiler jusqu'à la dernière page pour débloquer la signature")}

                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>
                                Progression de lecture
                            </Typography>
                            <Typography variant="caption" fontWeight="700" sx={{ color: hasScrolledToEnd ? '#16a34a' : primaryHex, fontFamily }}>
                                {hasScrolledToEnd ? '✓ Lu entièrement' : `${Math.round(scrollProgress)}%`}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={scrollProgress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: hasScrolledToEnd ? '#16a34a' : primaryHex,
                                    borderRadius: 3,
                                },
                            }}
                        />
                    </Box>

                    <Box
                        ref={scrollRef}
                        sx={{
                            height: 520,
                            overflowY: 'auto',
                            bgcolor: '#525659',
                            borderRadius: 1.5,
                            p: 1,
                            scrollbarWidth: 'thin',
                            scrollbarColor: `${primaryHex} #ccc`,
                        }}
                    >
                        {isLoadingPreview && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 1.5 }}>
                                <CircularProgress sx={{ color: 'white' }} size={32} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Chargement du document pré-rempli…
                                </Typography>
                            </Box>
                        )}
                        {!isLoadingPreview && (
                            <Document
                                file={previewBlobUrl ?? pdfPath}
                                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                                onLoadError={(err) => console.error('[CooperativeDocumentStep] PDF load error:', err)}
                                loading={
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                        <CircularProgress sx={{ color: 'white' }} />
                                    </Box>
                                }
                            >
                                {Array.from({ length: numPages }, (_, i) => (
                                    <Box key={i} sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Page
                                            pageNumber={i + 1}
                                            width={pdfWidth}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                    </Box>
                                ))}
                            </Document>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: hasScrolledToEnd ? '#f0fdf4' : '#fffbeb', border: '1px solid', borderColor: hasScrolledToEnd ? '#bbf7d0' : '#fde68a' }}>
                        {hasScrolledToEnd
                            ? <LockOpenIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                            : <LockIcon sx={{ color: '#d97706', fontSize: 18 }} />
                        }
                        <Typography variant="caption" fontWeight="600" sx={{ color: hasScrolledToEnd ? '#166534' : '#92400e', fontFamily }}>
                            {hasScrolledToEnd
                                ? 'Document lu entièrement, vous pouvez maintenant signer.'
                                : "Faites défiler jusqu'à la fin du document pour débloquer la signature."}
                        </Typography>
                    </Box>
                </Box>
            </Grid>

            {/* ── 3. Signing section ── */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        bgcolor: hasScrolledToEnd ? 'background.paper' : 'grey.100',
                        opacity: hasScrolledToEnd ? 1 : 0.45,
                        pointerEvents: hasScrolledToEnd ? 'auto' : 'none',
                        transition: 'opacity 0.4s',
                        position: 'relative',
                    }}
                >
                    {!(hasScrolledToEnd) && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <LockIcon sx={{ color: '#d97706', fontSize: 36 }} />
                                <Typography variant="body2" fontWeight="700" color="#92400e" fontFamily={fontFamily} textAlign="center">
                                    Lisez le document en entier pour débloquer la signature
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {sectionTitle(
                        <DrawIcon />,
                        'Signature & mentions obligatoires',
                        'Complétez les trois étapes pour finaliser votre adhésion',
                    )}

                    {/* Step indicators */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                        {stepIndicators.map(({ key, label }) => {
                            const current = stepOrder.indexOf(signStep);
                            const thisIdx = stepOrder.indexOf(key as SignStep);
                            const isDone = current > thisIdx || signStep === 'done';
                            const isActive = key === signStep;
                            return (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, border: '1px solid', borderColor: isDone ? '#bbf7d0' : isActive ? primaryHex : 'grey.300', bgcolor: isDone ? '#f0fdf4' : isActive ? `${primaryHex}12` : 'transparent' }}>
                                    {isDone
                                        ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 14 }} />
                                        : <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: isActive ? primaryHex : 'grey.300' }} />
                                    }
                                    <Typography variant="caption" fontWeight="600" sx={{ color: isDone ? '#166534' : isActive ? primaryHex : 'text.secondary', fontFamily }}>
                                        {label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* ── Step 1: Signature ── */}
                        {signStep === 'signature' && (
                            <Box sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontFamily, mb: 0.5 }}>
                                    Votre signature
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily, mb: 1.5 }}>
                                    Signez dans le cadre ci-dessous. Vérifiez que votre signature est lisible avant de valider.
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <FormInput
                                        label="Fait à (ville)"
                                        value={faitA}
                                        onChange={e => setFaitA(e.target.value)}
                                        required
                                    />
                                </Box>
                                {showSignPad ? (
                                    <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.300', p: 1, position: 'relative' }}>
                                        {!signatureDataURL && (
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', textAlign: 'center' }}>
                                                <Typography variant="body2" color="grey.400" fontWeight="600" fontFamily={fontFamily}>
                                                    Signer ici
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                            <button
                                                onClick={handleSignatureClear}
                                                className="text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                                            >
                                                Effacer
                                            </button>
                                        </Box>
                                        <SignaturePad
                                            ref={padRef}
                                            onEnd={handleSignatureTrim}
                                            onBegin={() => setSignatureDataURL(null)}
                                            velocityFilterWeight={0.2}
                                            maxWidth={1.5}
                                            minWidth={1}
                                            canvasProps={{ className: 'w-full', style: { height: 160, cursor: 'crosshair' } }}
                                        />
                                    </Box>
                                ) : (
                                    signatureDataURL && (
                                        <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid #bbf7d0', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <img src={signatureDataURL} alt="Signature" style={{ maxHeight: 64, maxWidth: '60%', objectFit: 'contain' }} />
                                            <ButtonField label="Modifier" variant="outlined" className="font-bold" onClick={handleSignatureClear} />
                                        </Box>
                                    )
                                )}
                                {showSignPad && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                                        <ButtonField
                                            label="Valider la signature"
                                            variant="contained"
                                            className="font-bold"
                                            onClick={handleSignatureValidate}
                                            disabled={!signatureDataURL || !faitA.trim()}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Already-signed summary */}
                        {signStep !== 'signature' && signatureDataURL && (
                            <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                <img src={signatureDataURL} alt="Signature validée" style={{ maxHeight: 40, objectFit: 'contain' }} />
                                <Typography variant="caption" fontWeight="700" color="#166534" fontFamily={fontFamily}>Signature validée</Typography>
                            </Box>
                        )}

                        {/* ── Step 2: Mention lu et approuvé (VT: bon pour adhésion) ── */}
                        {signStep !== 'signature' && (
                            <Box>
                                {isVerifiedTrainer ? (
                                    <MentionInput
                                        expected={MENTION_1_EXPECTED}
                                        display="Bon pour adhésion"
                                        label="Mention d'adhésion à la coopérative"
                                        description='Tapez exactement la mention « Bon pour adhésion » pour confirmer votre adhésion à la coopérative Verified Trainer SAP.'
                                        onValidated={() => setSignStep('mention2')}
                                        primaryHex={primaryHex}
                                        fontFamily={fontFamily}
                                    />
                                ) : (
                                    <MentionInput
                                        expected={MENTION_LU_EXPECTED}
                                        display="Lu et approuvé"
                                        label="Mention obligatoire"
                                        description='Tapez exactement la mention « Lu et approuvé » pour valider votre adhésion à la coopérative.'
                                        onValidated={() => setSignStep('mention2')}
                                        primaryHex={primaryHex}
                                        fontFamily={fontFamily}
                                    />
                                )}
                            </Box>
                        )}

                        {/* ── Step 3: Mention mandat (VT and SCOOP) ── */}
                        {(signStep === 'mention2' || signStep === 'done') && (
                            <MentionInput
                                expected={MENTION_2_EXPECTED}
                                display="Bon pour mandat de facturation"
                                label="Mention de mandat de facturation"
                                description='Tapez exactement la mention « Bon pour mandat de facturation » pour autoriser la coopérative à facturer en votre nom.'
                                onValidated={() => setSignStep('done')}
                                primaryHex={primaryHex}
                                fontFamily={fontFamily}
                            />
                        )}

                        {/* ── Done: generate & confirm ── */}
                        {signStep === 'done' && (
                            <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="700" color="#166534" fontFamily={fontFamily}>
                                            Toutes les étapes sont complétées !
                                        </Typography>
                                        <Typography variant="body2" color="#166534" fontFamily={fontFamily}>
                                            Vous pouvez télécharger votre exemplaire signé, puis confirmer votre adhésion.
                                        </Typography>
                                    </Box>
                                </Box>
                                {downloadUrl ? (
                                    <div className="flex lg:flex-row flex-col gap-y-2 justify-between pt-2">
                                        <a href={downloadUrl} download={'reglement-interieur-coop-signe.pdf'} style={{ textDecoration: 'none' }}>
                                            <ButtonField
                                                label="Télécharger mon exemplaire"
                                                variant="outlined"
                                                className="font-bold"
                                                startIcon={<DownloadIcon />}
                                            />
                                        </a>
                                        <ButtonField
                                            label="Finaliser mon adhésion →"
                                            variant="contained"
                                            className="font-bold"
                                            onClick={() => nextStep(isVerifiedTrainer ? 5 : 6)}
                                        />
                                    </div>
                                ) : (
                                    <ButtonField
                                        label={isGenerating ? 'Génération en cours...' : 'Générer le document signé'}
                                        variant="contained"
                                        className="font-bold"
                                        onClick={handleSubmit}
                                        disabled={isGenerating}
                                        startIcon={isGenerating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Settings>
    );
};

export default CooperativeDocumentStep;
